// src/app/accept-invitation/accept-invitation.component.ts

import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { InvitationService } from '../../services/invitation.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AcceptedInvitationResponse, InvitationResponse } from '../../models/invitation-response.model';
import { ApplicationService } from '../../services/application.service';
import { InvitationCommunicationService } from '../../services/invitation-communication.service';
import { DataStorageService } from '../../services/data-storage.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-accept-invitation',
  templateUrl: './accept-invitation.component.html',
  styleUrls: ['./accept-invitation.component.scss']
})
export class AcceptInvitationComponent implements OnInit, OnDestroy {

  invitationToken: string = '';
  isLoading: boolean = true;
  errorMessage: string = '';
  role: 'borrower' | 'supplier' | 'unknown' = 'unknown';
  isDirect: boolean = false;
  senderAccountId?: number;
  message: string = '';
  private destroy$ = new Subject<void>();
  private inactivityTimer: any;
  private readonly INACTIVITY_TIMEOUT = 300000; // 5 minutes in milliseconds

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invitationService: InvitationService,
    private applicationService: ApplicationService,
    private dataStorageService: DataStorageService,
    private snackBar: MatSnackBar,
    private commService: InvitationCommunicationService
  ) { }

  ngOnInit(): void {
    // Clear application cache on page refresh
    this.clearApplicationCache();
    this.resetInactivityTimer();

    this.invitationToken = this.route.snapshot.paramMap.get('token') || '';
    if (this.invitationToken) {
      this.fetchInvitationDetails(this.invitationToken);
    } else {
      this.errorMessage = 'Invalid invitation token.';
      this.isLoading = false;
      this.showError();
    }

    // Subscribe to submitApplication event
    this.commService.submitApplication$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.submitApplication();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
  }

  @HostListener('document:mousemove')
  @HostListener('document:keypress')
  @HostListener('document:click')
  @HostListener('document:scroll')
  onUserActivity(): void {
    this.resetInactivityTimer();
  }

  private resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    this.inactivityTimer = setTimeout(() => {
      this.clearApplicationCache();
      window.location.reload();
    }, this.INACTIVITY_TIMEOUT);
  }

  /**
   * Clear all application data from storage
   */
  private clearApplicationCache(): void {
    // Clear all session storage data
    sessionStorage.clear();
  }

  /**
   * Fetches the invitation details without accepting it.
   * @param token The invitation token.
   */
  fetchInvitationDetails(token: string): void {
    this.invitationService.getInvitation(token).subscribe({
      next: (response: InvitationResponse | AcceptedInvitationResponse) => {
        if ('isAccepted' in response && response.isAccepted) {
          this.handleAcceptedInvitation(response as AcceptedInvitationResponse);
        } else {
          this.handlePendingInvitation(response as InvitationResponse);
        }
      },
      error: (error) => {
        // Error path - invitation is invalid for some reason
        this.isLoading = false;
        this.errorMessage = error || 'An error occurred while retrieving the invitation.';
        this.showError();
      }
    });
  }

  /**
   * Helper function to handle valid (not yet accepted) invitations
   * 
   * @param response The invitation response from the API
   */
  private handlePendingInvitation(response: InvitationResponse): void {
    // Happy path - only happens if invitation is valid
    this.isLoading = false;
    this.message = response.message || '';
    this.role = toLowerCaseRole(response.role) || 'unknown';
    this.isDirect = response.isDirect || false;
    this.senderAccountId = response.senderAccountId;
    this.commService.role = toLowerCaseRole(response.role) || 'unknown';
    this.commService.applicationGuid = response.applicationGuid || '';

    // Check if personal details form is empty and redirect if needed
    const personalDetails = this.dataStorageService.getItem('personalDetailsForm');
    if (!personalDetails) {
      this.router.navigate(['personal-details'], { relativeTo: this.route });
      return;
    }

    // Show success message
    this.snackBar.open(this.message, 'Close', { duration: 5000 });
  }

  /**
   * Helper function to handle already accepted invitations
   * 
   * @param response The accepted invitation response from the API
   */
  private handleAcceptedInvitation(response: AcceptedInvitationResponse): void {
    const status = response.applicationStatus.toLowerCase() ?? null;
    if (!status) {
      this.errorMessage = 'Invalid application status in the accepted invitation.';
      this.showError();
      return;
    }

    // Route to the appropriate application status page with the application state
    this.router.navigate(['application', status], { state: response });
  }

  /**
   * Displays error messages using MatSnackBar or other UI elements.
   */
  showError(): void {
    if (this.errorMessage) {
      this.snackBar.open(this.errorMessage, 'Close', { duration: 5000 });
    }
  }

  /**
   * Collects all data stored in dataStorage by child components
   * and sends it to the API as a single object.
   */
  submitApplication(): void {
    this.isLoading = true;

    // Check if role is valid for application service
    if (this.role === 'unknown') {
      this.isLoading = false;
      this.snackBar.open('Error: Unknown user role.', 'Close', { duration: 3000 });
      return;
    }

    // List of dataStorage keys used by each child component
    const storageKeys = [
      'aboutBusinessForm',
      'businessStructureForm',
      'businessLocationForm',
      'ownershipDisclosure',
      'creditLineForm'
    ];

    // Object that will collect all data
    const applicationData: any = {
      invitationToken: this.invitationToken,
      applicationGuid: this.commService.applicationGuid,
    };

    // Collect data from each dataStorage key
    storageKeys.forEach(key => {
      const data = this.dataStorageService.getItem(key);
      if (data) {
        // Merge data into the main object
        Object.assign(applicationData, data);
      }
    });

    // Always add personal details as the first owner
    Object.assign(applicationData, {
      owners: [this.dataStorageService.getItem('personalDetailsForm')],
    });

    // Add owners if noIndiviualOver25 is false
    const ownershipDisclosure = this.dataStorageService.getItem<{noIndividualOver25: 'yes' | 'no'}>('ownershipDisclosureForm');
    if (ownershipDisclosure?.noIndividualOver25 === "yes") {
      // Remove internal IDs from the owners array
      const additionalOwners = this.dataStorageService.getItem('additionalOwners') as Array<any>;
      const ownersWithoutIds = additionalOwners ? additionalOwners.map((owner: any) => {
        const { id, ...ownerWithoutId } = owner;
        return ownerWithoutId;
      }) : [];

      Object.assign(applicationData, {
        owners: [
          this.dataStorageService.getItem('personalDetailsForm'),
          ...ownersWithoutIds
        ]
      });
    }

    // Safe type casting for role at this point
    const validRole = this.role as 'borrower' | 'supplier';
    console.log('Submitting application data:', applicationData);
    // Send data to the API
    this.applicationService.updateApplication(validRole, applicationData).subscribe({
      next: () => {
        // Happy path - application updated and invitation accepted
        this.isLoading = false;
        this.snackBar.open('Application updated and invitation accepted successfully!', 'Close', { duration: 3000 });

        // Navigate to thank you page
        this.router.navigate(['thank-you'], { relativeTo: this.route });
      },
      error: (error) => {
        console.error('Error updating application:', error);
        this.isLoading = false;
        this.snackBar.open('An error occurred while updating your application.', 'Close', { duration: 3000 });
      }
    });
  }
}

const toLowerCaseRole = (role: 'BORROWER' | 'SUPPLIER'): 'borrower' | 'supplier' => {
  return role.toLowerCase() as 'borrower' | 'supplier';
};
